/* global Office */
import { useMemo, useRef } from "react";
import { getIconSize } from "../../helpers/iconUtils";
import { prepareRichInline } from "@chenglou/pretext/rich-inline";

export function getDPIRatio() {
  return typeof Office !== "undefined" ? (Office.context.platform === Office.PlatformType.Mac ? 0.75 : 1) : 1;
}

export function countMinimumLines(json) {
  if (!json?.content) return 1;
  let lines = 0;
  for (const node of json.content) {
    if (node.type === "paragraph") {
      lines += 1;
      lines += (node.content ?? []).filter((n) => n.type === "hardBreak").length;
    } else if (node.type === "bulletList" || node.type === "orderedList") {
      for (const item of node.content ?? []) {
        lines += 1;
        for (const para of item.content ?? []) {
          lines += (para.content ?? []).filter((n) => n.type === "hardBreak").length;
        }
      }
    }
  }
  return Math.max(1, lines);
}

export const LIST_INDENT_UL_PX = 18;
export const LIST_INDENT_OL_PX = 30;

export function countParagraphBlocks(json) {
  if (!json?.content) return 1;
  let count = 0;
  for (const node of json.content) {
    if (node.type === "paragraph") {
      count++;
    } else if (node.type === "bulletList" || node.type === "orderedList") {
      for (const item of node.content ?? []) {
        count += (item.content ?? []).filter((n) => n.type === "paragraph").length;
      }
    }
  }
  return Math.max(1, count);
}

export function extractRichItems(nodes, defaultFont) {
  const result = [];
  for (const n of nodes.filter((n) => n.type === "text")) {
    const marks = n.marks ?? [];
    const isBold = marks.some((m) => m.type === "bold");
    const isItalic = marks.some((m) => m.type === "italic");
    const textStyle = marks.find((m) => m.type === "textStyle");
    const family = textStyle?.attrs?.fontFamily ?? defaultFont.family;
    const weight = isBold ? "bold" : defaultFont.weight;
    const style = isItalic ? "italic" : defaultFont.style;
    const sizePx = parseInt(defaultFont.size?.replace("pt", "") ?? "12") * getDPIRatio() * (4 / 3);
    const font = `${style} ${weight} ${sizePx}px ${family}`;
    const text = n.text ?? "";
    const leadingWs = text.match(/^\s+/)?.[0] ?? "";
    // Attach any leading whitespace to the previous word so the line-breaker sees
    // the correct breakable space at cross-node boundaries.
    if (leadingWs && result.length > 0) {
      result[result.length - 1] = { ...result[result.length - 1], text: result[result.length - 1].text + leadingWs };
    }
    const words = text.slice(leadingWs.length).match(/\S+\s*/g) ?? [];
    for (const word of words) {
      result.push({ text: word, font, break: "never" });
    }
  }
  return result;
}

export function splitByHardBreaks(nodes) {
  const segments = [];
  let current = [];
  for (const node of nodes) {
    if (node.type === "hardBreak") {
      segments.push(current);
      current = [];
    } else {
      current.push(node);
    }
  }
  segments.push(current);
  return segments;
}

export function extractLineSegments(json, defaultFont) {
  const segments = [];
  if (!json?.content) return segments;
  const addParagraph = (paraNode, indent) => {
    for (const seg of splitByHardBreaks(paraNode.content ?? [])) {
      const items = extractRichItems(seg, defaultFont);
      segments.push({ items, indent });
    }
  };
  for (const node of json.content) {
    if (node.type === "paragraph") {
      addParagraph(node, 0);
    } else if (node.type === "bulletList") {
      for (const item of node.content ?? []) {
        for (const para of item.content ?? []) {
          if (para.type === "paragraph") addParagraph(para, LIST_INDENT_UL_PX);
        }
      }
    } else if (node.type === "orderedList") {
      for (const item of node.content ?? []) {
        for (const para of item.content ?? []) {
          if (para.type === "paragraph") addParagraph(para, LIST_INDENT_OL_PX);
        }
      }
    }
  }
  return segments;
}

export function useCellStyleCalcs(json, currentStyle, currentStyleSheet) {
  // --- Padding / layout ---
  const verticalAlign =
    currentStyle?.verticalAlign === "middle"
      ? "center"
      : currentStyle?.verticalAlign === "bottom"
        ? "end"
        : currentStyle?.verticalAlign === "top"
          ? "start"
          : (currentStyle?.verticalAlign ?? "center");

  const defaultPadding = parseInt(currentStyleSheet?.cellPadding ?? "0");
  const paddingLeft = currentStyle?.paddingLeft ?? currentStyle?.padding ?? defaultPadding;
  const paddingLeftPerc = currentStyle?.paddingLeftPerc ?? currentStyle?.paddingPerc ?? 0;
  const pl = `calc(${paddingLeftPerc}% + ${paddingLeft}px)`;
  const paddingRight = currentStyle?.paddingRight ?? currentStyle?.padding ?? defaultPadding;
  const paddingRightPerc = currentStyle?.paddingRightPerc ?? currentStyle?.paddingPerc ?? 0;
  const pr = `calc(${paddingRightPerc}% + ${paddingRight}px)`;
  const width = `calc(100% - ${paddingLeft + paddingRight}px - ${paddingLeftPerc + paddingRightPerc}%)`;

  const paddingTop = currentStyle?.paddingTop ?? currentStyle?.padding ?? defaultPadding;
  const paddingTopPerc = currentStyle?.paddingTopPerc ?? currentStyle?.paddingPerc ?? 0;
  const pt = `calc(${paddingTopPerc}% + ${paddingTop}px)`;
  const paddingBottom = currentStyle?.paddingBottom ?? currentStyle?.padding ?? defaultPadding;
  const paddingBottomPerc = currentStyle?.paddingBottomPerc ?? currentStyle?.paddingPerc ?? 0;
  const pb = `calc(${paddingBottomPerc}% + ${paddingBottom}px)`;
  const height = `calc(100% - ${paddingTop + paddingBottom}px - ${paddingTopPerc + paddingBottomPerc}%)`;

  const paddingH = paddingLeft + paddingRight;
  const paddingHPerc = paddingLeftPerc + paddingRightPerc;
  const paddingV = paddingTop + paddingBottom;
  const paddingVPerc = paddingTopPerc + paddingBottomPerc;

  // --- Line spacing ---
  const currentLineSpacing = currentStyleSheet?.lineSpacing?.find((ls) => ls.id === currentStyle?.size);
  const lineHeight =
    currentLineSpacing?.line ??
    (currentStyleSheet?.lineSpacing?.length > 0
      ? currentStyleSheet?.lineSpacing[
          parseInt(currentStyle?.size?.replace("pt", "") ?? "0") < 8 ? 0 : currentStyleSheet.lineSpacing.length - 1
        ].line
      : 1.3);
  const paragraphSpacing = currentLineSpacing?.paragraph ?? 0;
  const fontSizePx = parseInt(currentStyle?.size?.replace("pt", "") ?? "12") * getDPIRatio() * (4 / 3);
  const lineHeightPx = fontSizePx * lineHeight;
  const paragraphSpacingPx = paragraphSpacing * getDPIRatio() * (4 / 3);

  // --- Cell content metrics ---
  const lineCount = countMinimumLines(json);
  const blockCount = countParagraphBlocks(json);
  const minimumCellHeight = lineCount * lineHeightPx + blockCount * paragraphSpacingPx;

  // --- Shape / icon / container geometry ---
  const shape = currentStyle?.shape;
  const shapeMarginLeft = shape?.marginLeft ?? shape?.margin ?? 0;
  const shapeMarginRight = shape?.marginRight ?? shape?.margin ?? 0;
  const shapeMarginTop = shape?.marginTop ?? shape?.margin ?? 0;
  const shapeMarginBottom = shape?.marginBottom ?? shape?.margin ?? 0;
  const shapeMarginLeftPerc = shape?.marginLeftPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginRightPerc = shape?.marginRightPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginTopPerc = shape?.marginTopPerc ?? shape?.marginPerc ?? 0;
  const shapeMarginBottomPerc = shape?.marginBottomPerc ?? shape?.marginPerc ?? 0;
  const shapeInnerHPerc = shape?.type === "Circle" ? 30 : shape?.type === "ArrowFull" ? 15 : 0;
  const shapeInnerVPerc = shape?.type === "Circle" ? 30 : 0;

  const shapeMarginH = shapeMarginLeft + shapeMarginRight;
  const shapeMarginHPerc = shapeMarginLeftPerc + shapeMarginRightPerc;
  const shapeMarginV = shapeMarginTop + shapeMarginBottom;
  const shapeMarginVPerc = shapeMarginTopPerc + shapeMarginBottomPerc;

  const iconHasHorizontalImpact =
    !!currentStyle?.icon && currentStyle?.iconAlign !== "center" && !!currentStyle?.iconAlign;
  const iconTotalWidth = (() => {
    if (!iconHasHorizontalImpact) return 0;
    const iSize = getIconSize(currentStyle.iconSize);
    const iMarginLeft = currentStyle.iconStyle?.marginLeft ?? currentStyle.iconStyle?.margin ?? 0;
    const iMarginRight = currentStyle.iconStyle?.marginRight ?? currentStyle.iconStyle?.margin ?? 0;
    const iEnclosurePadding =
      currentStyle.iconEnclosure && currentStyle.iconEnclosure !== "none" ? Math.max(iSize * 0.1 + 4, 4) : 0;
    return iSize + iMarginLeft + iMarginRight + 2 * iEnclosurePadding;
  })();

  // When iconAlign is "center" (or absent with an icon), CellTextWithIcon renders a
  // gridTemplateRows:"1fr 1fr" layout — the icon occupies the top half and text starts
  // below it, so available height for text is halved.
  const iconHasVerticalImpact =
    !!currentStyle?.icon && (currentStyle?.iconAlign === "center" || !currentStyle?.iconAlign);
  const iconTotalHeight = (() => {
    if (!iconHasVerticalImpact) return 0;
    const iSize = getIconSize(currentStyle.iconSize);
    const iMarginTop = currentStyle.iconStyle?.marginTop ?? currentStyle.iconStyle?.margin ?? 0;
    const iMarginBottom = currentStyle.iconStyle?.marginBottom ?? currentStyle.iconStyle?.margin ?? 0;
    const iEnclosurePadding =
      currentStyle.iconEnclosure && currentStyle.iconEnclosure !== "none" ? Math.max(iSize * 0.1 + 4, 4) : 0;
    return iSize + iMarginTop + iMarginBottom + 2 * iEnclosurePadding;
  })();

  const scaleV = (1 - shapeMarginVPerc / 100) * (1 - shapeInnerVPerc / 100) * (1 - paddingVPerc / 100);
  const offsetV = shapeMarginV * (1 - shapeInnerVPerc / 100) * (1 - paddingVPerc / 100) + paddingV;

  const containerParams = {
    shapeMarginH,
    shapeMarginHPerc,
    shapeInnerHPerc,
    paddingH,
    paddingHPerc,
    iconTotalWidth,
  };

  // --- Rich-text layout ---
  // Resolve effective font size the same way CellTextEditor does: fall back to the stylesheet
  // default preset when currentStyle has no explicit size set.
  const effectiveFontSize = (() => {
    if (currentStyle?.size) return currentStyle.size;
    if (!currentStyle?.preset && currentStyleSheet) {
      const defaultPreset = currentStyleSheet.presets?.find((p) => p.name === currentStyleSheet.defaults?.preset);
      if (defaultPreset?.fontSize) return defaultPreset.fontSize;
    }
    return "12pt";
  })();
  const defaultFont = {
    size: effectiveFontSize,
    family: currentStyle?.fontFamily ?? "Arial",
    weight: currentStyle?.weight === "bold" ? "bold" : "normal",
    style: currentStyle?.style === "italic" ? "italic" : "normal",
  };
  const preparedSegments = useMemo(
    () =>
      extractLineSegments(json ?? null, defaultFont).map(({ items, indent }) => ({
        prepared: items.length > 0 ? prepareRichInline(items) : null,
        indent,
      })),
    [json, currentStyle]
  );

  // --- Single consolidated ref (safe to read from ResizeObserver / mount effects) ---
  const styleCalcsRef = useRef({});
  styleCalcsRef.current = {
    preparedSegments,
    lineCount,
    lineHeightPx,
    blockCount,
    paragraphSpacingPx,
    minimumCellHeight,
    containerParams,
    scaleV,
    offsetV,
    paddingV,
    paddingVPerc,
    iconHasVerticalImpact,
    iconTotalHeight,
  };

  return {
    verticalAlign,
    pl,
    pr,
    pt,
    pb,
    width,
    height,
    lineHeight,
    paragraphSpacing,
    fontSizePx,
    lineHeightPx,
    paragraphSpacingPx,
    lineCount,
    blockCount,
    containerParams,
    scaleV,
    offsetV,
    preparedSegments,
    styleCalcsRef,
    minimumCellHeight,
  };
}
